import React from 'react';
import Yup from '~/util/yup';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { DateFormField } from '~/shared/forms/controls/fields/date-fields/DateFormField';
import type { DeclinedImmunizationModel } from '../types';
import { FormModal } from '~/shared/forms/FormModal';
import {
	addPatientDeclinedImmunizationOptions,
	getImmunizationsOptions,
	ImmunizationTypeOptions,
	editPatientDeclinedImmunizationOptions,
	ImmunizationTypeEnum
} from '@generated';
import { useQuery } from '@tanstack/react-query';

const validationSchema = Yup.object().shape({
	immunizationTypeId: Yup.string().label('Immunization Type').nullable(),
	immunizationTypeOther: Yup.string().label('Immunization Type (Other)').nullable().otherReason('immunizationTypeId', ImmunizationTypeEnum.Other),
	dateDeclined: Yup.date().label('Date Declined').nullable().required(),
	reasonForDecline: Yup.string().label('Reason For Decline').nullable().required()
});

export function DeclinedImmunizationFormModal({
	stayId,
	existingImmunization,
	quickAddImmunizationTypeId,
	closeModal
}: {
	stayId: string;
	existingImmunization?: DeclinedImmunizationModel;
	quickAddImmunizationTypeId?: number;
	closeModal: () => void;
}) {
	const { data: patientImmunizations, isLoading } = useQuery(getImmunizationsOptions(stayId));

	if (isLoading || !patientImmunizations) {
		return null;
	}

	const initialValues = existingImmunization
		? {
				...existingImmunization,
				immunizationId: existingImmunization.id
		  }
		: {
				stayId,
				immunizationTypeId: quickAddImmunizationTypeId || null,
				immunizationTypeOther: null,
				dateDeclined: null,
				reasonForDecline: null
		  };

	return (
		<FormModal
			title={`${existingImmunization ? 'Edit' : 'Add'} Declined Immunization`}
			initialValues={initialValues}
			saveMutation={{
				...(existingImmunization ? editPatientDeclinedImmunizationOptions : addPatientDeclinedImmunizationOptions),
				invalidateQueryKeys: [getImmunizationsOptions(stayId).queryKey]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const formValues = formHelpers?.values;
				return (
					<div className="grid grid-cols-1 gap-4">
						<SelectFormField name="immunizationTypeId" options={ImmunizationTypeOptions} />
						{formValues?.immunizationTypeId === '99' && <TextFormField name="immunizationTypeOther" />}
						<DateFormField name="dateDeclined" />
						<TextFormField name="reasonForDecline" />
					</div>
				);
			}}
		</FormModal>
	);
}
